import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, lastValueFrom, interval, Subscription } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { ConfigService } from './config.service';

/**
 * Status of a Codex CLI auth session
 */
export type CodexAuthStatus =
  | 'starting'       // Session is starting up
  | 'waiting_code'   // Waiting for device code to appear
  | 'waiting'        // Device code captured, waiting for user
  | 'authenticated'  // Success!
  | 'needs_intervention' // Timeout, manual intervention needed
  | 'failed'         // Auth failed
  | 'cancelled';     // User cancelled

/**
 * Diagnostic information for debugging auth issues
 */
export interface CodexAuthDiagnostics {
  cli_path: string | null;
  cli_version: string | null;
  cli_found: boolean;
  ssh_bypass: boolean;
  trust_established: boolean;
  process_started: boolean;
  process_pid: number | null;
  process_exit_code: number | null;
  screen_session: string | null;
  url_captured: boolean;
  code_captured: boolean;
  url_capture_time_ms: number | null;
  credentials_found: boolean;
  credentials_check_count: number;
  raw_output_lines: number;
  raw_output_tail: string[];
  errors: string[];
  warnings: string[];
}

/**
 * Response from auth endpoints
 */
export interface CodexAuthSessionResponse {
  session_id: string;
  status: CodexAuthStatus;
  device_url: string | null;
  device_code: string | null;
  error: string | null;
  screen_session: string | null;
  diagnostics?: CodexAuthDiagnostics;
}

/**
 * Service for managing Codex CLI device code authentication wizard.
 *
 * This service provides a way to authenticate Codex CLI (for ChatGPT).
 * The flow:
 * 1. Call startAuth() - backend starts Codex CLI and captures device URL/code
 * 2. Display the URL and code to the user
 * 3. User visits URL and enters code in browser
 * 4. Poll with pollStatus() until authenticated
 *
 * Unlike Claude's flow, there's no code to paste back - Codex handles
 * the OAuth exchange internally once the user enters the code in browser.
 */
@Injectable({
  providedIn: 'root'
})
export class CodexAuthWizardService {
  /** Current auth session ID */
  private _sessionId: string | null = null;

  /** Device auth URL (for user to visit) */
  private _deviceUrl: string | null = null;

  /** Device code (for user to enter in browser) */
  private _deviceCode: string | null = null;

  /** Latest diagnostics */
  private _diagnostics: CodexAuthDiagnostics | null = null;

  /** Observable auth status */
  private _authStatus = new BehaviorSubject<CodexAuthStatus | null>(null);
  public authStatus$ = this._authStatus.asObservable();

  /** Observable error message */
  private _error = new BehaviorSubject<string | null>(null);
  public error$ = this._error.asObservable();

  /** Observable diagnostics */
  private _diagnostics$ = new BehaviorSubject<CodexAuthDiagnostics | null>(null);
  public diagnostics$ = this._diagnostics$.asObservable();

  /** Polling subscription */
  private _pollingSub: Subscription | null = null;

  /** Polling interval in ms */
  private readonly POLL_INTERVAL_MS = 2000;

  constructor(private http: HttpClient) {}

  /**
   * Get current session ID
   */
  get sessionId(): string | null {
    return this._sessionId;
  }

  /**
   * Get device URL for user to visit
   */
  get deviceUrl(): string | null {
    return this._deviceUrl;
  }

  /**
   * Get device code for user to enter
   */
  get deviceCode(): string | null {
    return this._deviceCode;
  }

  /**
   * Get current auth status
   */
  get currentStatus(): CodexAuthStatus | null {
    return this._authStatus.value;
  }

  /**
   * Get latest diagnostics
   */
  get diagnostics(): CodexAuthDiagnostics | null {
    return this._diagnostics;
  }

  /**
   * Check if auth is in progress
   */
  get isAuthInProgress(): boolean {
    const status = this._authStatus.value;
    return status === 'starting' || status === 'waiting_code' || status === 'waiting';
  }

  /**
   * Start a new Codex CLI device auth session.
   *
   * @returns CodexAuthSessionResponse with session_id, device_url, and device_code
   * @throws HttpErrorResponse on API error
   */
  async startAuth(credentialsId?: string): Promise<CodexAuthSessionResponse> {
    // Reset state
    this._sessionId = null;
    this._deviceUrl = null;
    this._deviceCode = null;
    this._diagnostics = null;
    this._error.next(null);
    this._authStatus.next('starting');
    this._diagnostics$.next(null);
    this.stopPolling();

    try {
      const q = credentialsId ? `?credentials_id=${encodeURIComponent(credentialsId)}` : '';
      const response = await lastValueFrom(
        this.http.post<CodexAuthSessionResponse>(
          `${ConfigService.muxUrl}/backends/chatgpt/auth/start${q}`,
          {}
        )
      );

      this._sessionId = response.session_id;
      this._deviceUrl = response.device_url;
      this._deviceCode = response.device_code;
      this._authStatus.next(response.status);

      if (response.diagnostics) {
        this._diagnostics = response.diagnostics;
        this._diagnostics$.next(response.diagnostics);
      }

      if (response.error) {
        this._error.next(response.error);
      }

      return response;
    } catch (error: any) {
      this._authStatus.next('failed');
      const errorMsg = error?.error?.detail || error?.message || 'Failed to start auth';
      this._error.next(errorMsg);
      throw error;
    }
  }

  /**
   * Poll the auth status once.
   *
   * @param includeDiagnostics If true, fetch full diagnostics (slower)
   * @returns Current CodexAuthSessionResponse
   * @throws HttpErrorResponse on API error
   */
  async pollStatus(includeDiagnostics: boolean = false): Promise<CodexAuthSessionResponse | null> {
    if (!this._sessionId) {
      return null;
    }

    try {
      const params: any = { session_id: this._sessionId };
      if (includeDiagnostics) {
        params.include_diagnostics = 'true';
      }

      const response = await lastValueFrom(
        this.http.get<CodexAuthSessionResponse>(
          `${ConfigService.muxUrl}/backends/chatgpt/auth/status`,
          { params }
        )
      );

      this._authStatus.next(response.status);

      if (response.diagnostics) {
        this._diagnostics = response.diagnostics;
        this._diagnostics$.next(response.diagnostics);
      }

      if (response.error) {
        this._error.next(response.error);
      }

      return response;
    } catch (error: any) {
      // 404 means session expired/not found
      if (error?.status === 404) {
        this._authStatus.next('failed');
        this._error.next('Auth session expired');
      }
      throw error;
    }
  }

  /**
   * Get the captured terminal output from the auth session.
   *
   * @param tailLines Number of lines to return (default 50)
   * @returns Terminal output string
   */
  async getOutput(tailLines: number = 50): Promise<string | null> {
    if (!this._sessionId) {
      return null;
    }

    try {
      const response = await lastValueFrom(
        this.http.get<{ output: string }>(
          `${ConfigService.muxUrl}/backends/chatgpt/auth/output`,
          { params: { session_id: this._sessionId, tail_lines: tailLines.toString() } }
        )
      );
      return response.output;
    } catch (error) {
      console.error('Failed to get auth output:', error);
      return null;
    }
  }

  /**
   * Get full diagnostics for the current session.
   *
   * @returns Full diagnostic information
   */
  async fetchDiagnostics(): Promise<CodexAuthDiagnostics | null> {
    if (!this._sessionId) {
      return null;
    }

    try {
      const response = await lastValueFrom(
        this.http.get<{ diagnostics: CodexAuthDiagnostics }>(
          `${ConfigService.muxUrl}/backends/chatgpt/auth/diagnostics`,
          { params: { session_id: this._sessionId } }
        )
      );
      this._diagnostics = response.diagnostics;
      this._diagnostics$.next(response.diagnostics);
      return response.diagnostics;
    } catch (error) {
      console.error('Failed to get auth diagnostics:', error);
      return null;
    }
  }

  /**
   * Start automatic polling for auth status.
   *
   * Polls every 2 seconds until status is terminal (authenticated/failed/cancelled).
   * Automatically stops when terminal state reached.
   */
  startPolling(): void {
    if (this._pollingSub) {
      return; // Already polling
    }

    if (!this._sessionId) {
      console.warn('Cannot start polling: no active auth session');
      return;
    }

    this._pollingSub = interval(this.POLL_INTERVAL_MS)
      .pipe(
        takeWhile(() => this.isAuthInProgress)
      )
      .subscribe(async () => {
        try {
          await this.pollStatus();
        } catch (error) {
          console.error('Polling error:', error);
          this.stopPolling();
        }
      });
  }

  /**
   * Stop polling for auth status.
   */
  stopPolling(): void {
    if (this._pollingSub) {
      this._pollingSub.unsubscribe();
      this._pollingSub = null;
    }
  }

  /**
   * Cancel the current auth session.
   */
  async cancelAuth(): Promise<void> {
    if (!this._sessionId) {
      return;
    }

    this.stopPolling();

    try {
      await lastValueFrom(
        this.http.post(
          `${ConfigService.muxUrl}/backends/chatgpt/auth/cancel`,
          null,
          { params: { session_id: this._sessionId } }
        )
      );
    } catch (error) {
      console.error('Failed to cancel auth:', error);
    }

    this._sessionId = null;
    this._deviceUrl = null;
    this._deviceCode = null;
    this._authStatus.next('cancelled');
  }

  /**
   * Remove stored ChatGPT/Codex credentials on the server.
   */
  async deauth(): Promise<void> {
    await lastValueFrom(
      this.http.delete(`${ConfigService.muxUrl}/backends/chatgpt/auth`)
    );
  }

  /**
   * Reset service state.
   */
  reset(): void {
    this.stopPolling();
    this._sessionId = null;
    this._deviceUrl = null;
    this._deviceCode = null;
    this._diagnostics = null;
    this._authStatus.next(null);
    this._error.next(null);
    this._diagnostics$.next(null);
  }
}
